import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, Eye, EyeOff, Images, RefreshCw } from 'lucide-react';
import adminStyleQualityService, {
  StyleQualityCandidateReason,
  StyleQualityData,
  StyleQualityRow,
} from '../../services/api/adminStyleQualityService';
import adminImageStyleService from '../../services/api/adminImageStyleService';
import StyleQualitySamplesDialog from './StyleQualitySamplesDialog';
import { clefCheckDescription, clefCheckLabel } from './clefCheckLabels';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyCheckbox,
  TaskyInput,
  TaskySelect,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
} from '../tasky';

const PERIODS = [7, 30, 90] as const;

const REASON_LABELS: Record<StyleQualityCandidateReason, string> = {
  low_total: '総合点が低い',
  low_clef: 'Clef不合格が多い',
};

type SortKey = 'sample_size' | 'total' | 'behavior' | 'clef' | 'positive' | 'negative';
type SortDir = 'asc' | 'desc';
type Visibility = 'all' | 'active' | 'inactive';
type ScoreKey = 'total' | 'behavior' | 'clef';
/** 下限・上限（含む）。空文字は条件なし */
type Range = { min: string; max: string };

interface Filters {
  ranges: Record<ScoreKey, Range>;
  /** null は設定の最小枚数（API の settings.min_samples）を使う */
  minSamples: string | null;
  candidatesOnly: boolean;
  visibility: Visibility;
}

const SCORE_LABELS: Record<ScoreKey, string> = {
  total: '総合',
  behavior: '行動',
  clef: 'Clef',
};

/** 総合点のよく使う範囲（平均が 50、候補の線が 40） */
const TOTAL_PRESETS: { label: string; range: Range }[] = [
  { label: '総合 50〜100', range: { min: '50', max: '100' } },
  { label: '総合 50以下', range: { min: '', max: '50' } },
  { label: '総合 40未満', range: { min: '', max: '39.9' } },
];

const emptyRange = (): Range => ({ min: '', max: '' });

const defaultFilters = (): Filters => ({
  ranges: { total: emptyRange(), behavior: emptyRange(), clef: emptyRange() },
  minSamples: null,
  candidatesOnly: false,
  visibility: 'all',
});

/** 既定から変えた条件の数（アコーディオンを閉じていても絞り込み中と分かるように） */
const activeFilterCount = (filters: Filters): number =>
  (Object.values(filters.ranges) as Range[]).reduce((n, r) => n + (r.min !== '' ? 1 : 0) + (r.max !== '' ? 1 : 0), 0) +
  (filters.minSamples !== null ? 1 : 0) +
  (filters.candidatesOnly ? 1 : 0) +
  (filters.visibility !== 'all' ? 1 : 0);

const inRange = (value: number, range: Range): boolean => {
  const min = range.min === '' ? null : Number(range.min);
  const max = range.max === '' ? null : Number(range.max);
  if (min !== null && !Number.isNaN(min) && value < min) return false;
  if (max !== null && !Number.isNaN(max) && value > max) return false;
  return true;
};

const sortValue = (row: StyleQualityRow, key: SortKey): number => {
  switch (key) {
    case 'sample_size':
      return row.sample_size;
    case 'positive':
      return row.rates.positive;
    case 'negative':
      return row.rates.negative;
    default:
      return row.scores[key];
  }
};

/** 点数は低い順（悪いものから）、枚数・率は多い順から始める */
const DEFAULT_DIR: Record<SortKey, SortDir> = {
  sample_size: 'desc',
  total: 'asc',
  behavior: 'asc',
  clef: 'asc',
  positive: 'desc',
  negative: 'desc',
};

const percent = (rate: number | null): string => (rate === null ? '—' : `${Math.round(rate * 100)}%`);

const scoreColor = (score: number): string => {
  if (score < 40) return 'bg-rose-500';
  if (score < 50) return 'bg-amber-400';
  return 'bg-emerald-500';
};

const ScoreBar: React.FC<{ score: number; strong?: boolean }> = ({ score, strong }) => (
  <div className="flex items-center gap-2 whitespace-nowrap">
    <span className={`w-7 text-right tabular-nums ${strong ? 'font-semibold text-dark' : 'text-gray-700'}`}>
      {Math.round(score)}
    </span>
    <div className="h-1.5 w-10 rounded-full bg-gray-100">
      <div className={`h-1.5 rounded-full ${scoreColor(score)}`} style={{ width: `${score}%` }} />
    </div>
  </div>
);

/**
 * スタイル品質。総合点の低い順に並べ、非表示候補を目立たせる。非表示にするかは管理者が決める。
 * 並べ替え・点数の範囲などの絞り込みは読み込んだ全件に対して画面側で行う。
 * 採点の定義はバックエンドの StyleQualityScoreService（_docs/features/style-quality-score.md）。
 */
const StyleQualityPanel: React.FC = () => {
  const [days, setDays] = useState<number>(30);
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'total', dir: 'asc' });
  const [sampleStyle, setSampleStyle] = useState<StyleQualityRow | null>(null);
  const [query, setQuery] = useState('');
  const [data, setData] = useState<StyleQualityData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      setData(await adminStyleQualityService.list(days));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'スタイル品質を読み込めませんでした。');
    } finally {
      setIsLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const minSamplesValue = filters.minSamples ?? String(data?.settings.min_samples ?? 10);

  const rows = useMemo(() => {
    if (!data) return [];
    const keyword = query.trim().toLowerCase();
    const min = Number(minSamplesValue) || 0;
    const filtered = data.styles.filter((row) => {
      if (filters.candidatesOnly && !row.is_hide_candidate) return false;
      if (row.sample_size < min) return false;
      if (filters.visibility === 'active' && !row.is_active) return false;
      if (filters.visibility === 'inactive' && row.is_active) return false;
      if (!(Object.keys(filters.ranges) as ScoreKey[]).every((key) => inRange(row.scores[key], filters.ranges[key])))
        return false;
      if (keyword && !`${row.name} ${row.style_id} ${row.category ?? ''}`.toLowerCase().includes(keyword)) return false;
      return true;
    });
    const sign = sort.dir === 'asc' ? 1 : -1;
    // 同点は枚数の多い順（根拠の多いものを上に）
    return filtered.sort(
      (a, b) => sign * (sortValue(a, sort.key) - sortValue(b, sort.key)) || b.sample_size - a.sample_size,
    );
  }, [data, filters, minSamplesValue, query, sort]);

  const filterCount = activeFilterCount(filters);

  const setRange = (key: ScoreKey, bound: keyof Range, value: string) =>
    setFilters((f) => ({
      ...f,
      ranges: { ...f.ranges, [key]: { ...f.ranges[key], [bound]: value } },
    }));

  const toggleSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: DEFAULT_DIR[key] },
    );

  const sortHeader = (key: SortKey, label: string, align?: 'right') => (
    <TaskyTableHeaderCell
      align={align}
      aria-sort={sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <TaskyButton
        size="sm"
        variant="ghost"
        onClick={() => toggleSort(key)}
        className={`-mx-1 h-auto gap-1 px-1 py-0.5 text-xs ${sort.key === key ? 'text-dark' : 'text-gray-500'}`}
      >
        {label}
        {sort.key !== key ? (
          <ArrowUpDown className="h-3 w-3 text-gray-300" />
        ) : sort.dir === 'asc' ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        )}
      </TaskyButton>
    </TaskyTableHeaderCell>
  );

  const candidateCount = useMemo(
    () => data?.styles.filter((row) => row.is_hide_candidate && row.is_active).length ?? 0,
    [data],
  );

  const handleToggle = async (row: StyleQualityRow) => {
    try {
      setTogglingId(row.style_id);
      setError(null);
      const updated = await adminImageStyleService.toggleStyle(row.style_id);
      setData((prev) =>
        prev
          ? {
              ...prev,
              styles: prev.styles.map((s) =>
                s.style_id === row.style_id ? { ...s, is_active: updated.is_active } : s,
              ),
            }
          : prev,
      );
    } catch (err: any) {
      setError(err?.response?.data?.message || '表示の切り替えに失敗しました。');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <section className="space-y-4">
      {error && <TaskyAlert tone="error">{error}</TaskyAlert>}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-dark">スタイル品質</h2>
          {data && (
            <p className="mt-1 text-sm text-gray-600">
              直近{data.days}日 {data.global.sample_size.toLocaleString()}枚・
              {data.global.styles}スタイル／平均＝50点／ 非表示候補{' '}
              <span className="font-semibold text-rose-600">{candidateCount}</span>
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <TaskyInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="スタイル名・ID"
            className="w-44"
            aria-label="スタイル検索"
          />
          <TaskySelect value={String(days)} onChange={(e) => setDays(Number(e.target.value))} aria-label="期間">
            {PERIODS.map((p) => (
              <option key={p} value={p}>
                直近{p}日
              </option>
            ))}
          </TaskySelect>
          <TaskyButton size="sm" variant="secondary" onClick={load} disabled={isLoading} aria-label="再読み込み">
            {isLoading ? <TaskySpinner size="sm" /> : <RefreshCw />}
          </TaskyButton>
        </div>
      </div>

      <div className="rounded-tasky-md border border-warm-border bg-white">
        <TaskyButton
          size="sm"
          variant="ghost"
          onClick={() => setIsFilterOpen((open) => !open)}
          aria-expanded={isFilterOpen}
          className="h-auto w-full justify-between rounded-tasky-md px-4 py-2.5 text-dark"
        >
          <span className="flex items-center gap-2">
            詳細検索
            {filterCount > 0 && <TaskyBadge variant="info">{filterCount}件の条件</TaskyBadge>}
            <span className="text-xs font-normal text-gray-500">{rows.length}件表示</span>
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
        </TaskyButton>
        {isFilterOpen && (
          <div className="space-y-3 border-t border-warm-border px-4 py-3 text-sm">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              {(Object.keys(SCORE_LABELS) as ScoreKey[]).map((key) => (
                <div key={key} className="flex items-center gap-2">
                  <span className="w-10 text-gray-600">{SCORE_LABELS[key]}</span>
                  <TaskyInput
                    type="number"
                    min={0}
                    max={100}
                    value={filters.ranges[key].min}
                    onChange={(e) => setRange(key, 'min', e.target.value)}
                    placeholder="0"
                    className="w-20"
                    aria-label={`${SCORE_LABELS[key]}の下限`}
                  />
                  <span className="text-gray-400">〜</span>
                  <TaskyInput
                    type="number"
                    min={0}
                    max={100}
                    value={filters.ranges[key].max}
                    onChange={(e) => setRange(key, 'max', e.target.value)}
                    placeholder="100"
                    className="w-20"
                    aria-label={`${SCORE_LABELS[key]}の上限`}
                  />
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {TOTAL_PRESETS.map(({ label, range }) => (
                <TaskyButton
                  key={label}
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setFilters((f) => ({
                      ...f,
                      ranges: { ...f.ranges, total: { ...range } },
                    }))
                  }
                >
                  {label}
                </TaskyButton>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <label className="flex items-center gap-2">
                <span className="text-gray-600">最小枚数</span>
                <TaskyInput
                  type="number"
                  min={0}
                  value={minSamplesValue}
                  onChange={(e) => setFilters((f) => ({ ...f, minSamples: e.target.value }))}
                  className="w-20"
                  aria-label="最小枚数"
                />
              </label>
              <TaskyCheckbox
                checked={filters.candidatesOnly}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    candidatesOnly: e.target.checked,
                  }))
                }
                label="非表示候補のみ"
              />
              <TaskySelect
                value={filters.visibility}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    visibility: e.target.value as Visibility,
                  }))
                }
                aria-label="表示状態"
              >
                <option value="all">表示中・非表示とも</option>
                <option value="active">表示中のみ</option>
                <option value="inactive">非表示のみ</option>
              </TaskySelect>
              <TaskyButton size="sm" variant="ghost" onClick={() => setFilters(defaultFilters())}>
                条件をリセット
              </TaskyButton>
            </div>
          </div>
        )}
      </div>

      {isLoading && !data ? (
        <div className="flex items-center justify-center py-10">
          <TaskySpinner size="md" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <TaskyTable>
            <TaskyTableHead>
              <TaskyTableRow hoverable={false}>
                <TaskyTableHeaderCell>スタイル</TaskyTableHeaderCell>
                {sortHeader('sample_size', '枚数', 'right')}
                {sortHeader('total', '総合')}
                {sortHeader('behavior', '行動')}
                {sortHeader('clef', 'Clef')}
                {sortHeader('positive', '好反応')}
                {sortHeader('negative', '不満')}
                <TaskyTableHeaderCell>判定</TaskyTableHeaderCell>
                <TaskyTableHeaderCell align="right">表示</TaskyTableHeaderCell>
              </TaskyTableRow>
            </TaskyTableHead>
            <TaskyTableBody>
              {rows.map((row) => (
                <TaskyTableRow
                  key={row.style_id}
                  className={
                    row.is_hide_candidate && row.is_active ? 'bg-rose-50/60' : !row.is_active ? 'opacity-60' : ''
                  }
                  data-testid={`style-quality-row-${row.style_id}`}
                >
                  <TaskyTableCell>
                    <div className="flex w-48 items-center gap-3">
                      {row.thumbnail_url ? (
                        <img
                          src={row.thumbnail_url}
                          alt=""
                          className="h-10 w-10 shrink-0 rounded object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-10 w-10 shrink-0 rounded bg-gray-100" />
                      )}
                      <div className="min-w-0">
                        <p className="line-clamp-2 text-sm font-medium text-dark" title={row.name}>
                          {row.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          #{row.style_id}
                          {row.category ? ` · ${row.category}` : ''}
                        </p>
                      </div>
                    </div>
                  </TaskyTableCell>
                  <TaskyTableCell align="right" className="whitespace-nowrap tabular-nums">
                    {row.sample_size}
                  </TaskyTableCell>
                  <TaskyTableCell>
                    <ScoreBar score={row.scores.total} strong />
                  </TaskyTableCell>
                  <TaskyTableCell>
                    <ScoreBar score={row.scores.behavior} />
                  </TaskyTableCell>
                  <TaskyTableCell>
                    <div className="flex flex-col">
                      <ScoreBar score={row.scores.clef} />
                      <span className="whitespace-nowrap text-xs text-gray-500">
                        合格 {row.counts.clef_passed}/{row.counts.clef_evaluated}
                      </span>
                      {row.clef_failed_checks.length > 0 && (
                        <span
                          className="text-xs text-red-700"
                          title={row.clef_failed_checks
                            .map(({ check, count }) => `${clefCheckDescription(check)}：${count}枚`)
                            .join('\n')}
                        >
                          {row.clef_failed_checks
                            .slice(0, 3)
                            .map(({ check, count }) => `${clefCheckLabel(check)}${count}`)
                            .join('・')}
                        </span>
                      )}
                    </div>
                  </TaskyTableCell>
                  <TaskyTableCell className="whitespace-nowrap text-xs text-gray-600">
                    <p className="font-medium text-gray-800">{percent(row.rates.positive)}</p>
                    <p title="詳細を開いた / DL / お気に入り / リサイズ">
                      詳細{row.counts.detail_opened}・DL{row.counts.downloaded}
                      <br />♡{row.counts.favorited}・リサイズ
                      {row.counts.resized}
                    </p>
                  </TaskyTableCell>
                  <TaskyTableCell className="whitespace-nowrap text-xs text-gray-600">
                    <p className="font-medium text-gray-800">{percent(row.rates.negative)}</p>
                    <p title="編集 / 作り直し / 削除">
                      編集{row.counts.edited}・作り直し{row.counts.regenerated}
                      <br />
                      削除{row.counts.deleted}
                    </p>
                  </TaskyTableCell>
                  <TaskyTableCell>
                    {row.is_hide_candidate ? (
                      <div className="flex flex-col gap-1 whitespace-nowrap">
                        {row.candidate_reasons.map((reason) => (
                          <TaskyBadge key={reason} variant="destructive">
                            {REASON_LABELS[reason]}
                          </TaskyBadge>
                        ))}
                      </div>
                    ) : row.sample_size < (data?.settings.min_samples ?? 0) ? (
                      <span className="text-xs text-gray-400">母数不足</span>
                    ) : null}
                  </TaskyTableCell>
                  <TaskyTableCell align="right" className="whitespace-nowrap">
                    <div className="flex flex-col items-end gap-1">
                      <TaskyButton
                        size="sm"
                        variant="ghost"
                        onClick={() => setSampleStyle(row)}
                        data-track="admin-style-quality-samples"
                      >
                        <Images />
                        サンプル
                      </TaskyButton>
                      <TaskyButton
                        size="sm"
                        variant={row.is_active ? 'outline' : 'secondary'}
                        onClick={() => handleToggle(row)}
                        disabled={togglingId === row.style_id}
                        data-track="admin-style-quality-toggle"
                      >
                        {row.is_active ? <EyeOff /> : <Eye />}
                        {row.is_active ? '非表示にする' : '再表示'}
                      </TaskyButton>
                    </div>
                  </TaskyTableCell>
                </TaskyTableRow>
              ))}
              {rows.length === 0 && (
                <TaskyTableRow hoverable={false}>
                  <TaskyTableCell colSpan={9} className="py-8 text-center text-sm text-gray-500">
                    該当なし
                  </TaskyTableCell>
                </TaskyTableRow>
              )}
            </TaskyTableBody>
          </TaskyTable>
        </div>
      )}
      <StyleQualitySamplesDialog
        key={`${sampleStyle?.style_id ?? 'none'}-${days}`}
        style={sampleStyle}
        days={days}
        onClose={() => setSampleStyle(null)}
      />
    </section>
  );
};

export default StyleQualityPanel;
